import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { Category, Product } from '@/api/products'
import { productsApi } from '@/api/products'
import { suppliersApi } from '@/api/suppliers'
import { AppShell } from '@/components/AppShell'
import { Sheet } from '@/components/Sheet'
import { IconBox, IconClose, IconSearch, IconSettings, IconStar } from '@/components/icons'
import { Alert, Button, EmptyState, SkeletonList } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { useProductsCache } from '@/features/products/cache'
import { ProductForm } from '@/features/products/ProductForm'
import { toast } from '@/lib/toast'
import { useAuthStore } from '@/stores/authStore'

/** Φίλτρο κατηγορίας: όλες, συγκεκριμένη ή «χωρίς κατηγορία». */
const ALL = 'all'
const NONE = 'none'

/**
 * Ο κατάλογος της επιχείρησης: τι παραγγέλνει, σε ποια κατηγορία και από ποιον.
 *
 * Η αφετηρία είναι το προϊόν και όχι ο προμηθευτής. Ένα προϊόν μπορεί να το
 * φέρνουν δύο, και τότε στη λίστα αγορών ο χρήστης διαλέγει επιτόπου.
 */
export default function ProductsPage() {
  const { t } = useTranslation()
  const translateError = useApiError()
  const canManage = useAuthStore((s) => s.hasPermission('item:manage'))

  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<string>(ALL)
  const [editing, setEditing] = useState<Product | 'new' | null>(null)
  const [managing, setManaging] = useState(false)

  const products = useQuery({
    queryKey: ['products'],
    queryFn: productsApi.list,
  })

  // Οι ενότητες ακολουθούν τη σειρά των κατηγοριών· τα αταξινόμητα στο τέλος.
  const sections = useMemo(() => {
    const data = products.data
    if (!data) return []
    const needle = search.trim().toLocaleLowerCase('el')
    const matches = data.products.filter(
      (product) =>
        (!needle || product.name.toLocaleLowerCase('el').includes(needle)) &&
        (filter === ALL ||
          (filter === NONE ? product.categoryId === null : product.categoryId === filter)),
    )
    const known = new Set(data.categories.map((category) => category.id))
    const result: { id: string; name: string; products: Product[] }[] = data.categories.map(
      (category) => ({
        id: category.id,
        name: category.name,
        products: matches.filter((product) => product.categoryId === category.id),
      }),
    )
    result.push({
      id: NONE,
      name: t('products.uncategorized'),
      products: matches.filter(
        (product) => product.categoryId === null || !known.has(product.categoryId),
      ),
    })
    return result.filter((section) => section.products.length > 0)
  }, [products.data, search, filter, t])

  const total = products.data?.products.length ?? 0
  const uncategorized = products.data?.products.filter((p) => p.categoryId === null).length ?? 0

  return (
    <AppShell
      title={t('products.title')}
      subtitle={t('products.subtitle')}
      action={canManage ? { label: t('products.add'), onClick: () => setEditing('new') } : null}
    >
      {products.isPending && <SkeletonList rows={5} />}
      {products.isError && <Alert>{translateError(products.error)}</Alert>}

      {products.data && total === 0 && (
        <EmptyState
          icon={<IconBox size={26} />}
          title={t('products.empty')}
          description={t('products.emptyHint')}
        />
      )}

      {products.data && total > 0 && (
        <>
          <div className="relative mb-3">
            <IconSearch
              size={18}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('products.searchPlaceholder')}
              className="h-touch w-full rounded-xl border border-gray-300 bg-white pl-10 pr-3 text-base text-gray-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          {/* Οι κατηγορίες είναι και φίλτρο και η είσοδος στη διαχείρισή τους. */}
          <div className="-mx-4 mb-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0">
            <Chip active={filter === ALL} onClick={() => setFilter(ALL)}>
              {t('products.allCategories')} · {total}
            </Chip>
            {products.data.categories.map((category) => (
              <Chip
                key={category.id}
                active={filter === category.id}
                onClick={() => setFilter(category.id)}
              >
                {category.name} · {category.productCount}
              </Chip>
            ))}
            {uncategorized > 0 && (
              <Chip active={filter === NONE} onClick={() => setFilter(NONE)}>
                {t('products.uncategorized')} · {uncategorized}
              </Chip>
            )}
            {canManage && (
              <button
                onClick={() => setManaging(true)}
                aria-label={t('products.manageCategories')}
                className="flex h-10 shrink-0 items-center gap-1.5 rounded-full border border-dashed border-gray-300 px-3 text-sm font-semibold text-gray-600 hover:border-brand-400 hover:text-brand-700"
              >
                <IconSettings size={15} />
                {t('products.categories')}
              </button>
            )}
          </div>

          {sections.length === 0 && (
            <p className="rounded-2xl border border-gray-200/80 bg-white p-6 text-center text-sm text-gray-500 shadow-card">
              {t('products.noResults')}
            </p>
          )}

          {sections.map((section) => (
            <section key={section.id} className="mb-5">
              <h2 className="mb-2 px-1 text-xs font-bold uppercase tracking-widest text-gray-500">
                {section.name}
              </h2>
              <ul className="flex flex-col gap-2">
                {section.products.map((product) => (
                  <li key={product.key}>
                    <ProductCard
                      product={product}
                      canManage={canManage}
                      onEdit={() => setEditing(product)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      )}

      {editing && (
        <Sheet
          title={editing === 'new' ? t('products.add') : t('products.edit')}
          onClose={() => setEditing(null)}
        >
          <ProductForm
            product={editing === 'new' ? undefined : editing}
            initialCategoryId={filter !== ALL && filter !== NONE ? filter : null}
            onDone={() => setEditing(null)}
          />
        </Sheet>
      )}

      {managing && products.data && (
        <Sheet title={t('products.manageCategories')} onClose={() => setManaging(false)}>
          <CategoryManager
            categories={products.data.categories}
            onDone={() => setManaging(false)}
          />
        </Sheet>
      )}
    </AppShell>
  )
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={
        'flex h-10 shrink-0 items-center whitespace-nowrap rounded-full px-3.5 text-sm font-semibold transition-colors ' +
        (active
          ? 'bg-brand-700 text-white'
          : 'border border-gray-200 bg-white text-gray-700 hover:border-brand-300')
      }
    >
      {children}
    </button>
  )
}

function ProductCard({
  product,
  canManage,
  onEdit,
}: {
  product: Product
  canManage: boolean
  onEdit: () => void
}) {
  const { t } = useTranslation()
  const translateError = useApiError()
  const applyProducts = useProductsCache()
  const [adding, setAdding] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const suppliers = useQuery({
    queryKey: ['suppliers', ''],
    queryFn: () => suppliersApi.list(),
    enabled: adding,
  })
  const others = (suppliers.data ?? []).filter(
    (supplier) =>
      supplier.connectionStatus !== 'blocked' &&
      !product.suppliers.some((s) => s.supplierId === supplier.id),
  )

  const favorite = useMutation({
    mutationFn: () => productsApi.update(product, { isFavorite: !product.isFavorite }),
    onSuccess: applyProducts,
  })
  const addSupplier = useMutation({
    mutationFn: (supplierId: string) => productsApi.addSupplier(product, supplierId),
    onSuccess: (data) => {
      applyProducts(data)
      setAdding(false)
    },
  })
  const removeSupplier = useMutation({
    mutationFn: productsApi.removeSupplier,
    onSuccess: applyProducts,
  })
  const remove = useMutation({
    mutationFn: () => productsApi.remove(product),
    onSuccess: (data) => {
      applyProducts(data)
      toast.success(t('products.deleted'))
    },
  })

  const error = favorite.error ?? addSupplier.error ?? removeSupplier.error ?? remove.error

  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white p-3 shadow-card">
      <div className="flex items-start gap-2">
        <button
          onClick={() => favorite.mutate()}
          disabled={!canManage}
          aria-label={t('items.toggleFavorite')}
          aria-pressed={product.isFavorite}
          className="flex size-10 shrink-0 items-center justify-center rounded-xl text-gray-300 hover:bg-gray-50 disabled:hover:bg-transparent"
        >
          <IconStar
            size={19}
            filled={product.isFavorite}
            className={product.isFavorite ? 'text-amber-500' : ''}
          />
        </button>

        <div className="min-w-0 flex-1 pt-1.5">
          <p className="font-semibold text-gray-900">
            {product.name}{' '}
            <span className="text-xs font-normal text-gray-500">
              · {t(`unitNames.${product.unit}`)}
            </span>
          </p>
          {product.note && <p className="mt-0.5 text-sm italic text-gray-600">{product.note}</p>}
        </div>

        {canManage && (
          <button
            onClick={onEdit}
            className="h-10 shrink-0 rounded-xl px-3 text-sm font-semibold text-brand-700 hover:bg-brand-50"
          >
            {t('products.editShort')}
          </button>
        )}
      </div>

      {error && (
        <div className="mt-2">
          <Alert>{translateError(error)}</Alert>
        </div>
      )}

      {/* Οι προμηθευτές του προϊόντος. Με δεύτερο, η λίστα δίνει επιλογή. */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-12">
        {product.suppliers.map((supplier) => (
          <span
            key={supplier.businessItemId}
            className="inline-flex min-h-8 items-center gap-1 rounded-lg bg-brand-50 pl-2.5 pr-1 text-xs font-semibold text-brand-800"
          >
            {supplier.supplierName}
            {supplier.price && (
              <span className="font-normal tabular-nums text-brand-700">
                · {Number(supplier.price).toFixed(2)} €
              </span>
            )}
            {supplier.connectionStatus !== 'active' && (
              <span className="font-normal text-amber-700">· {t('products.pending')}</span>
            )}
            {canManage && product.suppliers.length > 1 ? (
              <button
                onClick={() => removeSupplier.mutate(supplier.businessItemId)}
                aria-label={`${t('products.removeSupplier')}: ${supplier.supplierName}`}
                className="flex size-7 items-center justify-center rounded-md text-brand-700 hover:bg-brand-100"
              >
                <IconClose size={13} />
              </button>
            ) : (
              <span className="w-1.5" />
            )}
          </span>
        ))}

        {canManage && !adding && (
          <button
            onClick={() => setAdding(true)}
            className="min-h-8 rounded-lg px-2 text-xs font-semibold text-brand-700 hover:bg-brand-50"
          >
            + {t('products.addSupplier')}
          </button>
        )}
      </div>

      {adding && (
        <div className="mt-2 rounded-xl border border-gray-100 bg-gray-50 p-2 sm:ml-12">
          {suppliers.isPending && (
            <p className="p-2 text-sm text-gray-500">{t('common.loading')}</p>
          )}
          {suppliers.data && others.length === 0 && (
            <p className="p-2 text-sm text-gray-500">{t('products.noOtherSuppliers')}</p>
          )}
          <ul className="flex flex-col">
            {others.map((supplier) => (
              <li key={supplier.id}>
                <button
                  onClick={() => addSupplier.mutate(supplier.id)}
                  disabled={addSupplier.isPending}
                  className="flex w-full items-center rounded-lg px-3 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-white"
                >
                  {supplier.name}
                </button>
              </li>
            ))}
          </ul>
          <button
            onClick={() => setAdding(false)}
            className="mt-1 px-3 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700"
          >
            {t('common.cancel')}
          </button>
        </div>
      )}

      {canManage && (
        <div className="mt-1 flex justify-end">
          {confirmDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">
                {t('products.deleteConfirm', { name: product.name })}
              </span>
              <button
                onClick={() => remove.mutate()}
                className="h-9 rounded-lg bg-red-50 px-3 text-xs font-semibold text-red-700 hover:bg-red-100"
              >
                {t('common.delete')}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="h-9 rounded-lg px-2 text-xs font-semibold text-gray-500"
              >
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              className="h-9 rounded-lg px-2 text-xs font-semibold text-gray-400 hover:text-red-600"
            >
              {t('common.delete')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/** Προσθήκη, μετονομασία, σειρά και διαγραφή κατηγοριών. */
function CategoryManager({ categories, onDone }: { categories: Category[]; onDone: () => void }) {
  const { t } = useTranslation()
  const translateError = useApiError()
  const applyProducts = useProductsCache()
  const [name, setName] = useState('')
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () => productsApi.createCategory(name.trim()),
    onSuccess: async () => {
      setName('')
      applyProducts(await productsApi.list())
    },
  })
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      productsApi.renameCategory(id, name.trim()),
    onSuccess: (data) => {
      applyProducts(data)
      setRenaming(null)
    },
  })
  const remove = useMutation({
    mutationFn: productsApi.deleteCategory,
    onSuccess: (data) => {
      applyProducts(data)
      setDeleting(null)
    },
  })
  const reorder = useMutation({
    mutationFn: productsApi.reorderCategories,
    onSuccess: applyProducts,
  })

  const move = (index: number, delta: number) => {
    const ids = categories.map((category) => category.id)
    const target = index + delta
    if (target < 0 || target >= ids.length) return
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    reorder.mutate(ids)
  }

  const error = create.error ?? rename.error ?? remove.error ?? reorder.error

  return (
    <div className="flex flex-col gap-4">
      {error && <Alert>{translateError(error)}</Alert>}

      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (name.trim()) create.mutate()
        }}
      >
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={100}
          placeholder={t('products.fields.newCategoryPlaceholder')}
          aria-label={t('products.fields.newCategoryName')}
          className="h-touch min-w-0 flex-1 rounded-xl border border-gray-300 bg-white px-3.5 text-base text-gray-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
        />
        <Button
          type="submit"
          loading={create.isPending}
          disabled={!name.trim()}
          className="!w-auto"
        >
          {t('products.addCategory')}
        </Button>
      </form>

      <ul className="flex flex-col divide-y divide-gray-100 rounded-2xl border border-gray-200">
        {categories.map((category, index) => (
          <li key={category.id} className="flex items-center gap-2 px-3 py-2">
            {renaming?.id === category.id ? (
              <form
                className="flex flex-1 gap-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  if (renaming.name.trim()) rename.mutate(renaming)
                }}
              >
                <input
                  value={renaming.name}
                  autoFocus
                  maxLength={100}
                  aria-label={t('products.renameCategory')}
                  onChange={(event) => setRenaming({ id: category.id, name: event.target.value })}
                  className="h-10 min-w-0 flex-1 rounded-lg border border-gray-300 px-3 text-base outline-none focus:border-brand-600"
                />
                <button
                  type="submit"
                  className="h-10 rounded-lg bg-brand-700 px-3 text-sm font-semibold text-white"
                >
                  {t('common.save')}
                </button>
              </form>
            ) : deleting === category.id ? (
              <div className="flex flex-1 flex-col gap-2 py-1">
                <p className="text-sm text-gray-700">
                  {t('products.deleteCategoryConfirm', { name: category.name })}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => remove.mutate(category.id)}
                    className="h-9 rounded-lg bg-red-50 px-3 text-sm font-semibold text-red-700"
                  >
                    {t('common.delete')}
                  </button>
                  <button
                    onClick={() => setDeleting(null)}
                    className="h-9 rounded-lg px-3 text-sm font-semibold text-gray-500"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setRenaming({ id: category.id, name: category.name })}
                  className="min-w-0 flex-1 truncate py-2 text-left font-medium text-gray-900"
                  title={t('products.renameCategory')}
                >
                  {category.name}
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    {t('products.categoryCount', {
                      count: category.productCount,
                    })}
                  </span>
                </button>
                <button
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  aria-label={t('products.moveUp')}
                  className="flex size-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                >
                  ↑
                </button>
                <button
                  onClick={() => move(index, 1)}
                  disabled={index === categories.length - 1}
                  aria-label={t('products.moveDown')}
                  className="flex size-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                >
                  ↓
                </button>
                <button
                  onClick={() => setDeleting(category.id)}
                  aria-label={t('common.delete')}
                  className="flex size-9 items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600"
                >
                  <IconClose size={15} />
                </button>
              </>
            )}
          </li>
        ))}
      </ul>

      <Button variant="secondary" onClick={onDone}>
        {t('products.done')}
      </Button>
    </div>
  )
}
